import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CONSTANTS } from '../data.js';
import type { TutorialState } from '../types';

type TutorialPopupProps = {
  tutorialState: TutorialState;
  onNext: () => void;
  onClose: () => void;
};

const TutorialHighlight: React.FC<{ target: string; }> = ({ target }) => {
  const [style, setStyle] = useState<React.CSSProperties>({ display: 'none' });

  useEffect(() => {
    if (target === 'none') {
      setStyle({ display: 'none' });
      return;
    }

    const findTarget = () => {
      const element = document.querySelector(`[data-tutorial-target='${target}']`);
      if (element) {
        const rect = element.getBoundingClientRect();
        setStyle({
          position: 'fixed',
          left: `${rect.left - 8}px`,
          top: `${rect.top - 8}px`,
          width: `${rect.width + 16}px`,
          height: `${rect.height + 16}px`,
          border: `3px solid ${CONSTANTS.ui.theme.colors.primary}`,
          borderRadius: '12px',
          boxShadow: `0 0 20px ${CONSTANTS.ui.theme.colors.primary}, 0 0 10px ${CONSTANTS.ui.theme.colors.primary} inset`,
          transition: 'all 0.3s ease-in-out',
          pointerEvents: 'none',
          zIndex: 9998,
        });
      } else {
        // Hide if target not found, it might appear later
        setStyle({ display: 'none' });
      }
    };

    // Initial check
    findTarget();
    // Periodically check for element position, as it might move or appear
    const interval = setInterval(findTarget, 100); 
    return () => clearInterval(interval);
  }, [target]);

  return <motion.div style={style} initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} />;
};


export const TutorialPopup: React.FC<TutorialPopupProps> = ({ tutorialState, onNext, onClose }) => {
  if (!tutorialState.active) return null;

  const tutorialData = CONSTANTS.tutorials[tutorialState.active];
  if (!tutorialData) return null;

  const stepData = tutorialData.steps[tutorialState.step];
  if (!stepData) return null;

  const isLastStep = tutorialState.step === tutorialData.steps.length - 1;

  // Some steps might auto-advance and not require a button.
  // For simplicity, we'll always show a button.
  const handleNext = () => {
    if (isLastStep) {
      onClose();
    } else {
      onNext();
    }
  };

  return (
    <>
      <TutorialHighlight target={stepData.target} />
      <motion.div
        className="absolute bottom-20 left-1/2 -translate-x-1/2 w-[500px] max-w-[90vw] p-6 rounded-xl z-[9999]"
        style={{
          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
          border: `2px solid ${CONSTANTS.ui.theme.colors.primary}`,
          boxShadow: CONSTANTS.ui.theme.effects.ornateBoxShadow
        }}
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 50, opacity: 0 }}
      >
        <h3 className="font-cinzel text-xl font-bold mb-3" style={{ color: CONSTANTS.ui.theme.colors.primary }}>{stepData.title}</h3>
        <p className="font-crimson text-base mb-4" style={{ color: CONSTANTS.ui.theme.colors.text }}>{stepData.text}</p>
        <div className="flex justify-end">
          <button
            onClick={handleNext}
            className="font-crimson px-6 py-2 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
            style={{
              background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`,
              color: CONSTANTS.ui.theme.colors.background,
            }}
          >
            {isLastStep ? 'Finish' : 'Next'}
          </button>
        </div>
      </motion.div>
    </>
  );
};
